import React, { useState, useEffect, useCallback } from 'react';
import { X, Calendar, CheckCircle, Circle, Play, ArrowRight, Sparkle, Trophy, Lightning, CaretDown, CaretRight, Clock, Target, ListChecks, Envelope, Users, Megaphone, ChartBar, TrendUp, Warning, CheckSquare } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { useFormDefaults } from '@/store/usePayGBotContext';
import { useWorkflowProgress } from '@/store/workflowProgress';
import ToolLinks from './ToolLinks';
import BusinessMemoryBanner from './BusinessMemoryBanner';
import TaskExecutionMeta from './TaskExecutionMeta';
import WhyThisFitsYouBlock from './WhyThisFitsYouBlock';

const TASK_CATEGORY_ICONS = {
  'CONTENT': <Play size={14} weight="fill" className="text-blue-400" />,
  'EMAIL': <Envelope size={14} weight="fill" className="text-purple-400" />,
  'CUSTOMER': <Users size={14} weight="fill" className="text-emerald-400" />,
  'VISIBILITY': <Megaphone size={14} weight="fill" className="text-orange-400" />,
  'BUSINESS': <Target size={14} weight="fill" className="text-cyan-400" />,
};

const TASK_CATEGORY_COLORS = {
  'CONTENT': 'border-blue-500/30 bg-blue-500/10',
  'EMAIL': 'border-purple-500/30 bg-purple-500/10',
  'CUSTOMER': 'border-emerald-500/30 bg-emerald-500/10',
  'VISIBILITY': 'border-orange-500/30 bg-orange-500/10',
  'BUSINESS': 'border-cyan-500/30 bg-cyan-500/10',
};

const HEALTH_COLORS = {
  'emerald': { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', text: 'text-emerald-400' },
  'amber': { bg: 'bg-amber-500/20', border: 'border-amber-500/40', text: 'text-amber-400' },
  'red': { bg: 'bg-red-500/20', border: 'border-red-500/40', text: 'text-red-400' },
};

const RunMyWeekModal = ({ isOpen, onClose, onGenerateContent, user, onOpenWeeklyContentPlanner, onSuccess }) => {
  const formDefaults = useFormDefaults();
  const workflowProgress = useWorkflowProgress();
  
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [detailedTasks, setDetailedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  const [completingTask, setCompletingTask] = useState(false);
  const [viewMode, setViewMode] = useState('today'); // 'today', 'week', 'report'
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [weeklyContentAvailable, setWeeklyContentAvailable] = useState(false);

  // Check if user is an internal account (Founder, PayGBot team)
  const isInternalUser = user?.is_internal || user?.plan_tier === 'internal' || user?.role === 'admin';

  // Auto-fetch plan on modal open
  useEffect(() => {
    if (isOpen) {
      fetchPlanAuto();
      // Check if it's Sunday to auto-show report
      const today = new Date();
      if (today.getDay() === 0) {
        fetchWeeklyReport();
      }
      // Check for weekly content planner content (for internal users)
      if (isInternalUser) {
        checkWeeklyContentAvailability();
      }
    }
  }, [isOpen]);

  // Check if Weekly Content Planner has content for this week
  const checkWeeklyContentAvailability = async () => {
    try {
      const response = await api.get('/weekly-content/current');
      if (response.data?.success && response.data?.data?.posts?.length > 0) {
        setWeeklyContentAvailable(true);
      }
    } catch (error) {
      // Silently fail - this is just for enhanced UX
      void('Weekly content check skipped');
    }
  };

  // Fetch weekly report
  const fetchWeeklyReport = async () => {
    setReportLoading(true);
    try {
      const response = await api.get('/weekly-plan/report');
      if (response.data.has_report) {
        setWeeklyReport(response.data.report);
        // Auto-switch to report view on Sunday
        const today = new Date();
        if (today.getDay() === 0 && response.data.show_prominently) {
          setViewMode('report');
        }
      }
    } catch (error) {
      console.error('Failed to fetch weekly report:', error);
    } finally {
      setReportLoading(false);
    }
  };

  // Fetch or auto-create the weekly plan
  const fetchPlanAuto = async () => {
    setLoading(true);
    // ── Phase 2 — try v2 (asset-driven) first ────────────────────
    // 1) Read the most recent v2 pack from the asset registry.
    // 2) If empty, POST /generate to create + register 7 content
    //    pack assets (one LLM call, behind MIL when enabled).
    // 3) Translate the v2 pack into the existing modal's
    //    `weeklyPlan` shape so no UI redesign is needed.
    // 4) Refresh Today's Mission v2 so the dashboard updates
    //    immediately when the user closes this modal.
    // Falls back to legacy /weekly-plan/auto on any failure so the
    // customer experience never breaks.
    try {
      const v2Read = await api.get('/plan-my-week-v2');
      let posts = v2Read?.data?.posts || [];
      let pack = v2Read?.data?.pack || null;
      if (!posts.length) {
        const v2Gen = await api.post('/plan-my-week-v2/generate', {});
        posts = v2Gen?.data?.posts || [];
        pack = {
          source_id: v2Gen?.data?.pack_id,
          meta: { start_date: v2Gen?.data?.start_date, platform: v2Gen?.data?.platform },
        };
        if (posts.length) {
          toast.success('Your week is ready!');
          try { onSuccess?.({ meta: { title: 'Your week is planned.' } }); } catch (_) { /* noop */ }
        }
      }
      if (posts.length) {
        const today = new Date();
        const todayIdx = (today.getDay() + 6) % 7;
        const startISO = pack?.meta?.start_date || today.toISOString().slice(0, 10);
        const platform = pack?.meta?.platform || 'Instagram';
        const days = posts.slice(0, 7).map((p, i) => {
          const meta = p.meta || {};
          return {
            day_index: i,
            day_name: meta.post_day || ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'][i],
            date: meta.post_date || null,
            topic: p.title || `Post ${i + 1}`,
            caption: meta.caption || p.summary || '',
            cta: meta.cta || '',
            hashtags: meta.hashtags || [],
            video_prompt: meta.video_prompt || '',
            asset_id: p.id,
            asset_status: p.status,
            platform,
          };
        });
        const detailed = days.map((d) => ({
          day_index: d.day_index,
          day_name: d.day_name,
          date: d.date,
          tasks: [{
            id: `post-${d.asset_id}`,
            name: `Post Content #${d.day_index + 1} — ${d.topic}`,
            category: 'CONTENT',
            status: d.asset_status === 'published' ? 'completed' : 'pending',
            description: d.caption,
            cta: d.cta,
            asset_id: d.asset_id,
          }],
        }));
        const v2Plan = {
          id: pack?.source_id || 'v2-pack',
          source: 'v2',
          start_date: startISO,
          platform,
          days,
          detailed_tasks: detailed,
        };
        setWeeklyPlan(v2Plan);
        setDetailedTasks(detailed);
        setExpandedDay(todayIdx);
        findNextTask(detailed, todayIdx);
        if (days[todayIdx]?.topic) {
          workflowProgress.completeContentIdeas();
          workflowProgress.completeWeeklyPlan({ id: v2Plan.id, todaysTopic: days[todayIdx].topic });
        }
        // Refresh Today's Mission v2 in the background so the
        // dashboard shows the newly-registered content_pack asset.
        try { await api.get('/todays-mission-v2'); } catch (e) { /* non-fatal */ }
        setLoading(false);
        return;
      }
    } catch (v2Err) {
      console.warn('plan-my-week-v2 unavailable, falling back to legacy:', v2Err);
    }
    // ── Legacy fallback (unchanged) ──────────────────────────────
    try {
      const response = await api.get('/weekly-plan/auto');
      const data = response.data;
      
      setWeeklyPlan(data.plan);
      setDetailedTasks(data.plan?.detailed_tasks || []);
      
      // Auto-expand today
      const todayIdx = data.today_index;
      setExpandedDay(todayIdx);
      
      // Set current task to first incomplete task of today
      findNextTask(data.plan?.detailed_tasks || [], todayIdx);
      
      if (data.was_created) {
        toast.success('Your week is ready!');
        try { onSuccess?.({ meta: { title: 'Your week is planned.' } }); } catch (_) { /* noop */ }
      }
      
      // Update workflow progress
      if (data.plan?.days?.length > 0) {
        workflowProgress.completeContentIdeas();
        const todaysTopic = data.plan.days[todayIdx]?.topic;
        workflowProgress.completeWeeklyPlan({
          id: data.plan.id,
          todaysTopic
        });
      }
    } catch (error) {
      console.error('Failed to fetch weekly plan:', error);
      toast.error('Failed to load your week');
    } finally {
      setLoading(false);
    }
  };

  // Find next incomplete task
  const findNextTask = (tasks, startDayIdx = 0) => {
    for (let d = startDayIdx; d < tasks.length; d++) {
      const dayTasks = tasks[d]?.tasks || [];
      for (let t = 0; t < dayTasks.length; t++) {
        if (dayTasks[t].status !== 'completed') {
          setCurrentTask({ dayIndex: d, taskIndex: t, task: dayTasks[t] });
          return;
        }
      }
    }
    // All done - set to null
    setCurrentTask(null);
  };

  // Get contextual follow-up message based on task category
  const getFollowUpMessage = (category, taskName) => {
    const followUps = {
      'CONTENT': 'Now schedule it or share it on your platform!',
      'EMAIL': 'Great! Track opens and replies to measure engagement.',
      'CUSTOMER': 'Personal touch builds loyalty! Note any feedback you received.',
      'VISIBILITY': 'Consistency is key! Do this same action next week too.',
      'BUSINESS': 'One step closer to your goals. Keep the momentum!'
    };
    return followUps[category] || 'Keep building your business!';
  };

  // Complete a task and get next
  const handleCompleteTask = async (dayIndex, taskIndex, task) => {
    setCompletingTask(true);
    try {
      const response = await api.post('/weekly-plan/complete-task', {
        day_index: dayIndex,
        task_index: taskIndex,
        task_type: task.category?.toLowerCase() || 'content'
      });
      
      const result = response.data;
      
      // Update local state
      const updatedTasks = [...detailedTasks];
      if (updatedTasks[dayIndex]?.tasks?.[taskIndex]) {
        updatedTasks[dayIndex].tasks[taskIndex].status = 'completed';
      }
      setDetailedTasks(updatedTasks);
      
      // Handle next task with contextual guidance
      if (result.next_task) {
        if (result.same_day) {
          setCurrentTask({ 
            dayIndex, 
            taskIndex: taskIndex + 1, 
            task: result.next_task 
          });
          // Contextual message with follow-up tip
          toast.success(`Done! ${getFollowUpMessage(task.category, task.task)}`, {
            description: `Next up: ${result.next_task?.task?.slice(0, 50) || 'Your next task'}`
          });
        } else if (result.next_day_index !== undefined) {
          setExpandedDay(result.next_day_index);
          findNextTask(updatedTasks, result.next_day_index);
          toast.success(`Today complete! ${getFollowUpMessage(task.category, task.task)}`);
        }
      } else if (result.week_complete) {
        setCurrentTask(null);
        toast.success('All tasks done! 🎉 Check out your next actions below.');
      }
      
    } catch (error) {
      console.error('Failed to complete task:', error);
      toast.error('Failed to update task');
    } finally {
      setCompletingTask(false);
    }
  };

  // Generate content for a topic
  const handleGenerateForTopic = (topic) => {
    onGenerateContent(topic);
    onClose();
  };

  const getTodayIndex = () => {
    const today = new Date().getDay();
    return today === 0 ? 6 : today - 1;
  };

  const isToday = (dayIndex) => dayIndex === getTodayIndex();

  // Calculate progress
  const calculateProgress = () => {
    let completed = 0;
    let total = 0;
    detailedTasks.forEach(day => {
      (day.tasks || []).forEach(task => {
        total++;
        if (task.status === 'completed') completed++;
      });
    });
    return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  const progress = calculateProgress();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden bg-slate-900 rounded-2xl shadow-2xl border border-white/10 flex flex-col"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-5 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <Calendar size={18} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-white">Your Business Week</h2>
                <p className="text-xs text-slate-400">{progress.completed}/{progress.total} tasks done ({progress.percentage}%)</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* View Mode Toggle */}
              <div className="flex bg-white/5 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('today')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    viewMode === 'today' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    viewMode === 'week' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => {
                    setViewMode('report');
                    if (!weeklyReport) fetchWeeklyReport();
                  }}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors flex items-center space-x-1 ${
                    viewMode === 'report' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <ChartBar size={12} weight="fill" />
                  <span>Report</span>
                </button>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X size={16} className="text-white" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            <div className="mb-3">
              <BusinessMemoryBanner />
            </div>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-slate-400 text-sm">Loading your week...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Progress Bar */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex-1 bg-white/10 rounded-full h-2.5">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${progress.percentage}%` }}
                      transition={{ duration: 0.5 }}
                      className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2.5 rounded-full"
                    />
                  </div>
                  <span className="text-slate-400 text-xs font-medium min-w-[60px] text-right">
                    {progress.percentage}% done
                  </span>
                </div>

                {/* TODAY VIEW - Current Task Focus */}
                {viewMode === 'today' && currentTask && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 border-2 border-blue-500/50 rounded-xl p-5 shadow-lg"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Lightning size={18} className="text-yellow-400" weight="fill" />
                        <span className="text-white text-sm font-bold uppercase tracking-wide">Your Next Step</span>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${TASK_CATEGORY_COLORS[currentTask.task?.category] || 'bg-white/10'}`}>
                        {TASK_CATEGORY_ICONS[currentTask.task?.category]}
                        <span className="ml-1 text-white/80">{currentTask.task?.category}</span>
                      </span>
                    </div>
                    
                    <h3 className="text-white text-lg font-bold mb-2">{currentTask.task?.task}</h3>
                    
                    {/* Expanded Task Details */}
                    <div className="space-y-3 mb-4">
                      {currentTask.task?.what && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-blue-300 text-xs font-semibold uppercase tracking-wide mb-1">What to do</p>
                          <p className="text-white text-sm">{currentTask.task.what}</p>
                        </div>
                      )}
                      {currentTask.task?.how && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-emerald-300 text-xs font-semibold uppercase tracking-wide mb-1">How to do it</p>
                          <p className="text-slate-300 text-sm whitespace-pre-line">{currentTask.task.how}</p>
                        </div>
                      )}
                      {currentTask.task?.expected_outcome && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-purple-300 text-xs font-semibold uppercase tracking-wide mb-1">Expected Result</p>
                          <p className="text-slate-300 text-sm">{currentTask.task.expected_outcome}</p>
                        </div>
                      )}
                      {currentTask.task?.time_estimate && (
                        <div className="flex items-center space-x-2 text-slate-400 text-sm">
                          <Clock size={14} />
                          <span>{currentTask.task.time_estimate}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex space-x-3">
                      {/* If it's a CONTENT task, show appropriate action based on user type */}
                      {currentTask.task?.category === 'CONTENT' && weeklyPlan?.days?.[currentTask.dayIndex]?.topic && (
                        <>
                          {/* For internal users with pre-generated content, show shortcut */}
                          {isInternalUser && weeklyContentAvailable && onOpenWeeklyContentPlanner ? (
                            <button
                              onClick={() => {
                                onClose();
                                onOpenWeeklyContentPlanner();
                                toast.success('Opening your pre-generated content for today');
                              }}
                              className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center space-x-2"
                              data-testid="use-pregenerated-content-btn"
                            >
                              <Sparkle size={18} weight="fill" />
                              <span>Use Pre-Generated</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleGenerateForTopic(weeklyPlan.days[currentTask.dayIndex].topic)}
                              className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center space-x-2"
                              data-testid="create-content-btn"
                            >
                              <Play size={18} weight="fill" />
                              <span>Create Content</span>
                            </button>
                          )}
                        </>
                      )}
                      
                      <button
                        onClick={() => handleCompleteTask(currentTask.dayIndex, currentTask.taskIndex, currentTask.task)}
                        disabled={completingTask}
                        className={`${currentTask.task?.category === 'CONTENT' ? 'flex-1' : 'w-full'} py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center space-x-2 disabled:opacity-50`}
                        data-testid="complete-task-btn"
                      >
                        {completingTask ? (
                          <span>Saving...</span>
                        ) : (
                          <>
                            <CheckCircle size={18} weight="fill" />
                            <span>Done → Next</span>
                            <ArrowRight size={16} weight="bold" />
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* All tasks completed - Show Next Actions (NO DEAD ENDS) */}
                {viewMode === 'today' && !currentTask && !loading && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="space-y-4"
                  >
                    {/* Celebration Banner */}
                    <div className="bg-gradient-to-br from-emerald-600/30 to-teal-600/20 border-2 border-emerald-500/50 rounded-xl p-5 text-center">
                      <Trophy size={40} weight="fill" className="text-yellow-400 mx-auto mb-2" />
                      <h3 className="text-white text-lg font-bold mb-1">Today's Tasks Complete!</h3>
                      <p className="text-slate-300 text-sm">
                        Great momentum! Here's what to do next:
                      </p>
                    </div>

                    {/* NEXT ACTIONS - No Dead Ends */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <Lightning size={16} className="text-amber-400" weight="fill" />
                        <span className="text-amber-400 text-xs font-bold uppercase tracking-wider">Keep Building</span>
                      </div>
                      
                      <div className="space-y-2">
                        {/* Action 1: Create Content */}
                        <button
                          onClick={() => {
                            const todaysTopic = weeklyPlan?.days?.[getTodayIndex()]?.topic;
                            if (todaysTopic) {
                              onGenerateContent(todaysTopic);
                            } else {
                              onGenerateContent('');
                            }
                            onClose();
                          }}
                          className="w-full flex items-center justify-between p-3 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg transition-colors group"
                          data-testid="next-action-create-content"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-500/30 rounded-lg flex items-center justify-center">
                              <Play size={16} className="text-blue-400" weight="fill" />
                            </div>
                            <div className="text-left">
                              <p className="text-white text-sm font-medium">Create More Content</p>
                              <p className="text-slate-400 text-xs">Generate your next viral post</p>
                            </div>
                          </div>
                          <ArrowRight size={16} className="text-blue-400 group-hover:translate-x-1 transition-transform" />
                        </button>

                        {/* Action 2: Send Follow-up Email */}
                        <button
                          onClick={() => {
                            toast.info('Open your email and send a quick check-in to a recent customer or lead');
                            onClose();
                          }}
                          className="w-full flex items-center justify-between p-3 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg transition-colors group"
                          data-testid="next-action-send-email"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-purple-500/30 rounded-lg flex items-center justify-center">
                              <Envelope size={16} className="text-purple-400" weight="fill" />
                            </div>
                            <div className="text-left">
                              <p className="text-white text-sm font-medium">Send a Follow-up</p>
                              <p className="text-slate-400 text-xs">Check in with a customer or lead</p>
                            </div>
                          </div>
                          <ArrowRight size={16} className="text-purple-400 group-hover:translate-x-1 transition-transform" />
                        </button>

                        {/* Action 3: Schedule Next Post */}
                        <button
                          onClick={() => {
                            setViewMode('week');
                            toast.success('Review your weekly plan and prepare tomorrow\'s content');
                          }}
                          className="w-full flex items-center justify-between p-3 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 rounded-lg transition-colors group"
                          data-testid="next-action-schedule-post"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-emerald-500/30 rounded-lg flex items-center justify-center">
                              <Calendar size={16} className="text-emerald-400" weight="fill" />
                            </div>
                            <div className="text-left">
                              <p className="text-white text-sm font-medium">Plan Tomorrow</p>
                              <p className="text-slate-400 text-xs">See your week and prepare ahead</p>
                            </div>
                          </div>
                          <ArrowRight size={16} className="text-emerald-400 group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>

                    {/* Streak Reminder */}
                    <div className="text-center text-slate-500 text-xs">
                      Keep your streak alive - complete at least one task tomorrow!
                    </div>
                  </motion.div>
                )}

                {/* WEEK VIEW - All Days */}
                {viewMode === 'week' && weeklyPlan && (
                  <div className="space-y-2">
                    {/* Round 92 — Why This Fits You + feedback */}
                    <WhyThisFitsYouBlock
                      department="operations"
                      taskType="operator_planning"
                      recommendationId={weeklyPlan?.id || weeklyPlan?.week_id || null}
                      recommendationSummary="the weekly plan I built for you"
                      businessCtx={{
                        hours_per_week: weeklyPlan?.time_budget || undefined,
                      }}
                      className="mb-3"
                    />
                    {weeklyPlan.days?.map((day, dayIndex) => {
                      const dayTasks = detailedTasks[dayIndex]?.tasks || [];
                      const dayIsToday = isToday(dayIndex);
                      const dayComplete = dayTasks.every(t => t.status === 'completed');
                      const dayTasksCompleted = dayTasks.filter(t => t.status === 'completed').length;
                      const isExpanded = expandedDay === dayIndex;
                      
                      return (
                        <div key={dayIndex} className={`rounded-xl border transition-all ${
                          dayIsToday && !dayComplete
                            ? 'border-blue-500/50 bg-blue-500/10'
                            : dayComplete
                            ? 'border-emerald-500/30 bg-emerald-500/5 opacity-70'
                            : 'border-white/10 bg-white/5'
                        }`}>
                          {/* Day Header */}
                          <button
                            onClick={() => setExpandedDay(isExpanded ? null : dayIndex)}
                            className="w-full px-4 py-3 flex items-center justify-between"
                          >
                            <div className="flex items-center space-x-3">
                              {dayComplete ? (
                                <CheckCircle size={20} weight="fill" className="text-emerald-400" />
                              ) : (
                                <Circle size={20} className={dayIsToday ? 'text-blue-400' : 'text-slate-500'} />
                              )}
                              <div className="text-left">
                                <div className="flex items-center space-x-2">
                                  <span className={`text-sm font-semibold ${
                                    dayIsToday ? 'text-blue-400' : 'text-white'
                                  }`}>
                                    {day.day_name}
                                  </span>
                                  {dayIsToday && !dayComplete && (
                                    <span className="px-1.5 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded uppercase">
                                      Today
                                    </span>
                                  )}
                                </div>
                                <p className={`text-xs ${dayComplete ? 'text-slate-500' : 'text-slate-400'}`}>
                                  {day.topic?.slice(0, 50)}{day.topic?.length > 50 ? '...' : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-slate-500">
                                {dayTasksCompleted}/{dayTasks.length}
                              </span>
                              {isExpanded ? (
                                <CaretDown size={16} className="text-slate-400" />
                              ) : (
                                <CaretRight size={16} className="text-slate-400" />
                              )}
                            </div>
                          </button>
                          
                          {/* Expanded Tasks */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="px-4 pb-4 space-y-2">
                                  {/* Round 34 — tier-correct execution metadata */}
                                  {(day.where_to_use || day.tier_asset) && (
                                    <TaskExecutionMeta
                                      action={day.topic}
                                      whereToUse={day.where_to_use}
                                      tierAsset={day.tier_asset}
                                      testIdPrefix={`week-day-${dayIndex}`}
                                    />
                                  )}
                                  {dayTasks.map((task, taskIndex) => (
                                    <div 
                                      key={taskIndex}
                                      className={`p-3 rounded-lg border ${
                                        task.status === 'completed'
                                          ? 'border-emerald-500/20 bg-emerald-500/5 opacity-60'
                                          : TASK_CATEGORY_COLORS[task.category] || 'border-white/10 bg-white/5'
                                      }`}
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="flex items-start space-x-2 flex-1 min-w-0">
                                          <button
                                            onClick={() => {
                                              if (task.status !== 'completed') {
                                                handleCompleteTask(dayIndex, taskIndex, task);
                                              }
                                            }}
                                            disabled={task.status === 'completed' || completingTask}
                                            className="mt-0.5 flex-shrink-0"
                                          >
                                            {task.status === 'completed' ? (
                                              <CheckCircle size={18} weight="fill" className="text-emerald-400" />
                                            ) : (
                                              <Circle size={18} className="text-slate-500 hover:text-blue-400" />
                                            )}
                                          </button>
                                          <div className="min-w-0 flex-1">
                                            <div className="flex items-center space-x-2 mb-1">
                                              {TASK_CATEGORY_ICONS[task.category]}
                                              <span className="text-[10px] font-semibold text-slate-500 uppercase">
                                                {task.category}
                                              </span>
                                            </div>
                                            <p className={`text-sm ${
                                              task.status === 'completed' 
                                                ? 'text-slate-500 line-through' 
                                                : 'text-white'
                                            }`}>
                                              {task.task}
                                            </p>
                                            {task.time_estimate && task.status !== 'completed' && (
                                              <p className="text-xs text-slate-500 mt-1 flex items-center">
                                                <Clock size={12} className="mr-1" />
                                                {task.time_estimate}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                        
                                        {/* Create button for CONTENT tasks */}
                                        {task.category === 'CONTENT' && task.status !== 'completed' && (
                                          isInternalUser && weeklyContentAvailable && onOpenWeeklyContentPlanner ? (
                                            <button
                                              onClick={() => {
                                                onClose();
                                                onOpenWeeklyContentPlanner();
                                              }}
                                              className="ml-2 px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded transition-colors flex items-center space-x-1"
                                            >
                                              <Sparkle size={12} weight="fill" />
                                              <span>Pre-Gen</span>
                                            </button>
                                          ) : (
                                            <button
                                              onClick={() => handleGenerateForTopic(day.topic)}
                                              className="ml-2 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors flex items-center space-x-1"
                                            >
                                              <Play size={12} weight="fill" />
                                              <span>Create</span>
                                            </button>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* REPORT VIEW - Weekly Business Report */}
                {viewMode === 'report' && (
                  <div className="space-y-4">
                    {reportLoading ? (
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                        <p className="text-slate-400 text-sm">Generating your report...</p>
                      </div>
                    ) : weeklyReport ? (
                      <>
                        {/* Health Status Card */}
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`rounded-xl p-5 border-2 ${
                            HEALTH_COLORS[weeklyReport.health_status?.color]?.bg || 'bg-white/10'
                          } ${HEALTH_COLORS[weeklyReport.health_status?.color]?.border || 'border-white/20'}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <ChartBar size={20} weight="fill" className={HEALTH_COLORS[weeklyReport.health_status?.color]?.text || 'text-white'} />
                              <span className="text-white text-sm font-bold uppercase tracking-wide">Business Health</span>
                            </div>
                            <span className="text-2xl">{weeklyReport.health_status?.emoji}</span>
                          </div>
                          <h3 className={`text-xl font-bold mb-2 ${HEALTH_COLORS[weeklyReport.health_status?.color]?.text || 'text-white'}`}>
                            {weeklyReport.health_status?.message}
                          </h3>
                          <div className="flex items-center space-x-2 mt-3">
                            <div className="flex-1 bg-black/20 rounded-full h-3">
                              <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: `${weeklyReport.metrics?.completion_percentage || 0}%` }}
                                transition={{ duration: 0.8 }}
                                className={`h-3 rounded-full ${
                                  weeklyReport.health_status?.color === 'emerald' ? 'bg-emerald-500' :
                                  weeklyReport.health_status?.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                                }`}
                              />
                            </div>
                            <span className="text-white font-bold text-lg">
                              {weeklyReport.metrics?.completion_percentage}%
                            </span>
                          </div>
                        </motion.div>

                        {/* Metrics Grid */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                          className="grid grid-cols-2 gap-3"
                        >
                          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                            <div className="flex items-center space-x-2 mb-2">
                              <CheckSquare size={16} weight="fill" className="text-emerald-400" />
                              <span className="text-slate-400 text-xs uppercase">Tasks Completed</span>
                            </div>
                            <p className="text-white text-2xl font-bold">
                              {weeklyReport.metrics?.tasks_completed}
                              <span className="text-slate-500 text-sm font-normal">/{weeklyReport.metrics?.tasks_total}</span>
                            </p>
                          </div>
                          
                          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                            <div className="flex items-center space-x-2 mb-2">
                              <Warning size={16} weight="fill" className="text-amber-400" />
                              <span className="text-slate-400 text-xs uppercase">Tasks Skipped</span>
                            </div>
                            <p className="text-white text-2xl font-bold">
                              {weeklyReport.metrics?.tasks_skipped}
                            </p>
                          </div>
                          
                          <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/30">
                            <div className="flex items-center space-x-2 mb-2">
                              <Envelope size={16} weight="fill" className="text-purple-400" />
                              <span className="text-slate-400 text-xs uppercase">Emails Sent</span>
                            </div>
                            <p className="text-white text-2xl font-bold">
                              {weeklyReport.metrics?.emails_sent}
                              <span className="text-purple-400 text-xs font-normal ml-1">/ 2 target</span>
                            </p>
                          </div>
                          
                          <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/30">
                            <div className="flex items-center space-x-2 mb-2">
                              <Megaphone size={16} weight="fill" className="text-orange-400" />
                              <span className="text-slate-400 text-xs uppercase">Advertising</span>
                            </div>
                            <p className="text-white text-2xl font-bold">
                              {weeklyReport.metrics?.advertising_actions}
                            </p>
                          </div>
                          
                          <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/30 col-span-2">
                            <div className="flex items-center space-x-2 mb-2">
                              <Users size={16} weight="fill" className="text-emerald-400" />
                              <span className="text-slate-400 text-xs uppercase">Customer Follow-ups</span>
                            </div>
                            <p className="text-white text-2xl font-bold">
                              {weeklyReport.metrics?.customer_followups}
                            </p>
                          </div>
                        </motion.div>

                        {/* Next Week Focus */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className="bg-gradient-to-br from-blue-600/20 to-cyan-600/10 rounded-xl p-5 border border-blue-500/30"
                        >
                          <div className="flex items-center space-x-2 mb-4">
                            <TrendUp size={18} weight="fill" className="text-blue-400" />
                            <h3 className="text-white font-semibold">Next Week Focus</h3>
                          </div>
                          <div className="space-y-3">
                            {weeklyReport.next_week_focus?.map((focus, index) => (
                              <div key={index} className="flex items-start space-x-3">
                                <div className="w-6 h-6 bg-blue-500/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <span className="text-blue-400 text-xs font-bold">{index + 1}</span>
                                </div>
                                <div>
                                  <p className="text-white font-medium text-sm">{focus.action}</p>
                                  <p className="text-slate-400 text-xs mt-0.5">{focus.reason}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>

                        {/* Sunday Notice */}
                        {weeklyReport.is_sunday && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="text-center py-3"
                          >
                            <p className="text-slate-500 text-xs">
                              New week starts tomorrow. Keep building!
                            </p>
                          </motion.div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <ChartBar size={48} className="text-slate-600 mx-auto mb-3" />
                        <h3 className="text-white font-semibold mb-2">No Report Yet</h3>
                        <p className="text-slate-400 text-sm">
                          Complete some tasks to generate your weekly report.
                        </p>
                        <button
                          onClick={fetchWeeklyReport}
                          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          Generate Report
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer with Tool Links */}
          <div className="border-t border-white/10 px-5 py-3 bg-slate-900/95">
            {/* Tool Links for Weekly Plan */}
            <ToolLinks outputType="weekly_plan" className="mb-3" compact />

            {/* Keep-going bridge into Run My Business (no dead ends) */}
            <button
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(
                    new CustomEvent('paygbot:open-tool', { detail: { tool: 'run_my_business' } })
                  );
                }
                onClose();
              }}
              data-testid="open-run-my-business-btn"
              className="w-full mb-3 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white text-sm font-semibold rounded-xl transition-all flex items-center justify-center space-x-2 shadow-lg active:scale-[0.99]"
            >
              <Target size={16} weight="fill" />
              <span>Run My Business</span>
              <ArrowRight size={14} weight="bold" />
            </button>

            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Business + Customer + Visibility = Growth
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-400 text-sm font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default RunMyWeekModal;
